import { useEffect, useRef, useState } from 'react';
import { Copy, Info, WarningCircle } from '@phosphor-icons/react';
import { useBlocker, useNavigate } from 'react-router-dom';
import {
  CampaignEmailBuilder,
  type CampaignEmailBuilderHandle,
} from '@/modules/campaigns/components/campaign-email-builder/campaign-email-builder';
import { useToast } from '@/hooks/use-toast';
import { copyTextToClipboard } from '@/lib/clipboard';
import { ConfirmationModal } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import { Textarea } from '@/components/ui-kit/textarea';
import { useCreateTemplate, useUpdateTemplate } from '../../hooks/use-templates';
import {
  hasMeaningfulEmailContent,
  resolveTemplateCategory,
} from '../../utils/template-list.utils';
import { TEMPLATE_CATEGORIES, type Template, type TemplateCategory } from '../../types/templates.types';
import {
  getTemplateErrorMessage,
  getTemplateFieldErrorsFromUnknown,
  getTemplateSessionGuidance,
  isTemplateConflictError,
} from '../../utils/template-api-errors';

const NO_CATEGORY = 'Uncategorized';

type MutateTemplateFormProps = {
  mode: 'create' | 'edit';
  /** Required for mode="edit" — the template being edited, already loaded by the caller. */
  template?: Template;
};

/**
 * Shared Name/Category/Description + MailCraft content editor form, used by both
 * the create-template and edit-template pages so they stay pixel-identical.
 */
export const MutateTemplateForm = ({ mode, template }: MutateTemplateFormProps) => {
  const isEdit = mode === 'edit' && Boolean(template);
  const navigate = useNavigate();
  // MailCraft fires `onChange` once for its own initial content load, not just user
  // edits — skip that first call so the dirty flag only reflects real changes.
  const skipNextContentChangeRef = useRef(true);
  const { toast } = useToast();
  const builderRef = useRef<CampaignEmailBuilderHandle>(null);
  const { mutateAsync: createTemplate, isPending: isCreating } = useCreateTemplate();
  const { mutateAsync: updateTemplate, isPending: isUpdating } = useUpdateTemplate();

  const [name, setName] = useState(template?.name.trim() ?? '');
  const [category, setCategory] = useState<string>(
    resolveTemplateCategory(template?.category) ?? NO_CATEGORY
  );
  const [description, setDescription] = useState(template?.description ?? '');
  const [nameTouched, setNameTouched] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [htmlError, setHtmlError] = useState<string | null>(null);
  // A ref (not state) so clearing dirty and immediately navigating on save can't
  // race `useBlocker`'s predicate against a not-yet-committed re-render.
  const isDirtyRef = useRef(false);
  const setIsDirty = (value: boolean) => {
    isDirtyRef.current = value;
  };

  const trimmedName = name.trim();
  const isNameValid = trimmedName.length > 0;
  const isBusy = isEdit ? isUpdating : isCreating;

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirtyRef.current && currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isDirtyRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  const handleCopyTemplateId = async () => {
    if (!template) return;
    const copied = await copyTextToClipboard(template.itemId);
    if (copied) {
      toast({ variant: 'success', title: 'Template ID copied' });
    } else {
      toast({ variant: 'destructive', title: 'Could not copy template ID' });
    }
  };

  const handleSave = async () => {
    setNameTouched(true);
    setNameError(null);
    setCategoryError(null);
    setHtmlError(null);
    if (!isNameValid) return;

    try {
      const content = await builderRef.current?.exportContent();
      if (!content) {
        throw new Error('Could not export email content from the builder.');
      }

      if (!hasMeaningfulEmailContent(content.html)) {
        setHtmlError('Add some content before saving.');
        return;
      }

      const trimmedDescription = description.trim();
      const selectedCategory =
        category === NO_CATEGORY ? undefined : (category as TemplateCategory);

      if (isEdit && template) {
        const saved = await updateTemplate({
          templateId: template.itemId,
          input: {
            name: trimmedName,
            html: content.html,
            description: trimmedDescription,
            category: selectedCategory ?? null,
          },
        });

        toast({
          variant: 'success',
          title: 'Template updated',
          description: `"${saved.name}" was updated successfully.`,
        });
      } else {
        const saved = await createTemplate({
          name: trimmedName,
          html: content.html,
          description: trimmedDescription || undefined,
          category: selectedCategory,
        });

        toast({
          variant: 'success',
          title: 'Template created',
          description: `"${saved.name}" was added to your templates.`,
        });
      }

      setIsDirty(false);
      navigate('/templates');
    } catch (error) {
      if (isTemplateConflictError(error)) {
        setNameError('That name is taken — try a different one.');
        return;
      }

      const fieldErrors = getTemplateFieldErrorsFromUnknown(error);
      const nameMessage = fieldErrors.Name?.[0];
      const categoryMessage = fieldErrors.Category?.[0];
      const htmlMessage = fieldErrors.Html?.[0];

      if (nameMessage) setNameError(nameMessage);
      if (categoryMessage) setCategoryError(categoryMessage);
      if (htmlMessage) setHtmlError(htmlMessage);

      if (!nameMessage && !categoryMessage && !htmlMessage) {
        toast({
          variant: 'destructive',
          title: isEdit ? 'Failed to update template' : 'Failed to create template',
          description:
            getTemplateSessionGuidance(error) ??
            getTemplateErrorMessage(error, 'Unexpected error while saving.'),
        });
      }
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-[1320px] flex-col gap-6 pb-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-2">
          <h2 className="text-2xl font-bold tracking-tight">
            {isEdit ? 'Edit template' : 'New template'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {isEdit
              ? 'Update your email template content and details, then save your changes.'
              : 'Name it, categorize it, and write the content — the same content model a campaign uses.'}
          </p>
          {isEdit && template ? (
            <div className="inline-flex w-fit items-center gap-2 rounded-md border bg-white px-3 py-1.5 text-xs font-medium text-muted-foreground">
              <span className="tracking-wide">TEMPLATE ID</span>
              <code className="text-foreground">{template.itemId}</code>
              <button
                type="button"
                onClick={() => {
                  void handleCopyTemplateId();
                }}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Copy template ID"
              >
                <Copy className="size-3.5" aria-hidden />
              </button>
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/templates')}
            disabled={isBusy}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="bg-primary-600 hover:bg-primary-700 text-white"
            onClick={() => {
              void handleSave();
            }}
            disabled={isBusy}
          >
            {isBusy ? (isEdit ? 'Saving...' : 'Creating...') : 'Save template'}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 rounded-lg border bg-white p-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="mutate-template-name">
            Name <span className="text-destructive">*</span>
          </Label>
          <Input
            id="mutate-template-name"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              setNameError(null);
              setIsDirty(true);
            }}
            onBlur={() => setNameTouched(true)}
            placeholder="e.g. Monthly newsletter shell"
            maxLength={200}
            disabled={isBusy}
            aria-required
            aria-invalid={(nameTouched && !isNameValid) || Boolean(nameError)}
            className={
              (nameTouched && !isNameValid) || nameError
                ? 'border-destructive focus-visible:ring-destructive'
                : 'focus-visible:border-primary-600 focus-visible:ring-primary-600'
            }
          />
          {nameTouched && !isNameValid ? (
            <p className="text-xs text-destructive">Template name is required</p>
          ) : nameError ? (
            <p className="text-xs text-destructive">{nameError}</p>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="mutate-template-category">Category</Label>
          <Select
            value={category}
            onValueChange={(value) => {
              setCategory(value);
              setCategoryError(null);
              setIsDirty(true);
            }}
          >
            <SelectTrigger
              id="mutate-template-category"
              disabled={isBusy}
              aria-invalid={Boolean(categoryError)}
              className={categoryError ? 'border-destructive focus:ring-destructive' : undefined}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_CATEGORY}>Uncategorized</SelectItem>
              {TEMPLATE_CATEGORIES.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {categoryError ? (
            <p className="text-xs text-destructive">{categoryError}</p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Used to group templates on the library page.
            </p>
          )}
        </div>

        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="mutate-template-description">
            Description <span className="font-normal text-muted-foreground">(optional)</span>
          </Label>
          <Textarea
            id="mutate-template-description"
            value={description}
            onChange={(event) => {
              setDescription(event.target.value);
              setIsDirty(true);
            }}
            placeholder="What is this layout for?"
            maxLength={2000}
            disabled={isBusy}
            className="focus-visible:border-primary-600 focus-visible:ring-primary-600"
          />
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-lg border border-primary-100 bg-primary-50 p-4 text-sm text-primary-900">
        <Info className="mt-0.5 size-4 shrink-0 text-primary-600" aria-hidden />
        <p>
          There&apos;s no <strong>subject line</strong> here on purpose — subject lives on the
          campaign, not the template, since the same layout is often reused with a different
          subject each time.
        </p>
      </div>

      {htmlError ? (
        <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          <WarningCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
          <p>{htmlError}</p>
        </div>
      ) : null}

      <div className="flex flex-col gap-3 rounded-lg border border-neutral-200 bg-white p-5 shadow-e1">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-sm font-semibold">Content</h3>
          <p className="text-xs text-muted-foreground">
            Merge tags live in the editor&apos;s Variables tab.
          </p>
        </div>

        <CampaignEmailBuilder
          key={isEdit && template ? `edit-template-${template.itemId}` : 'create-template'}
          ref={builderRef}
          html={template?.html ?? ''}
          name={trimmedName || 'Untitled template'}
          layout="design"
          showInlineActions={false}
          hideOuterBox
          onContentChange={() => {
            if (skipNextContentChangeRef.current) {
              skipNextContentChangeRef.current = false;
              return;
            }
            setIsDirty(true);
          }}
        />
      </div>

      <ConfirmationModal
        open={blocker.state === 'blocked'}
        onOpenChange={(open) => {
          if (!open && blocker.state === 'blocked') {
            blocker.reset();
          }
        }}
        title="Leave without saving?"
        description="This template has unsaved changes. Leaving now will discard them."
        confirmText="Leave without saving"
        cancelText="Cancel"
        confirmClassName="bg-destructive hover:bg-destructive/90 text-white"
        onConfirm={() => {
          setIsDirty(false);
          if (blocker.state === 'blocked') {
            blocker.proceed();
          }
        }}
      />
    </div>
  );
};
